import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  BedDouble,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Star,
  X,
} from "lucide-react";
import { publicApi } from "../admin/api";
import { needsInvert, resolveImage } from "../lib/data";
import SEO from "../components/SEO";

export default function HotelDetail() {
  const { slug } = useParams();
  const [hotel, setHotel] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    publicApi
      .get(`/hotels/${slug}`)
      .then((r) => setHotel(r.data))
      .catch(() => setError(true));
    publicApi.get("/hotels").then((r) => setOthers(r.data)).catch(() => {});
  }, [slug]);

  // Keyboard nav + body scroll lock + preload neighbors while lightbox is open.
  // MUST be declared above any early return to keep hook order stable.
  const galleryLen = hotel?.gallery?.length || 0;
  useEffect(() => {
    if (lightbox < 0 || galleryLen === 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      else if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % galleryLen);
      else if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + galleryLen) % galleryLen);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Preload neighbor images for instant nav
    const list = hotel?.gallery || [];
    [(lightbox + 1) % galleryLen, (lightbox - 1 + galleryLen) % galleryLen].forEach((idx) => {
      if (list[idx]) {
        const img = new Image();
        img.src = resolveImage(list[idx]);
      }
    });

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, galleryLen, hotel]);

  if (error) {
    return (
      <div className="min-h-[70svh] grid place-items-center text-center px-6 pt-32">
        <div>
          <h1 className="font-display text-4xl">Hotel non trovato</h1>
          <Link
            to="/hotel"
            className="mt-6 inline-flex items-center gap-2 text-white/60 hover:text-white"
          >
            <ArrowLeft size={14} /> Torna al portafoglio
          </Link>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="min-h-[70svh] grid place-items-center text-white/40 pt-32">
        Caricamento…
      </div>
    );
  }

  const gallery = hotel.gallery?.length ? hotel.gallery : [];
  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(-1);
  const nextImg = () => setLightbox((i) => (i + 1) % gallery.length);
  const prevImg = () => setLightbox((i) => (i - 1 + gallery.length) % gallery.length);

  const relatedHotels = others.filter((h) => h.slug !== hotel.slug).slice(0, 3);

  return (
    <div data-testid="hotel-detail-page">
      <SEO
        title={hotel.name}
        path={`/hotel/${hotel.slug}`}
        description={`${hotel.tagline} ${hotel.description?.slice(0, 140) || ""}`}
        image={resolveImage(hotel.image)}
        type="article"
      />
      {/* Hero */}
      <section className="relative h-[88svh] min-h-[600px] w-full overflow-hidden">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${resolveImage(hotel.image)})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#08080A]" />
        </motion.div>

        {hotel.logo && (
          <img
            src={hotel.logo}
            alt={hotel.name}
            className={`absolute top-28 right-10 h-16 md:h-20 w-auto max-w-[200px] object-contain opacity-95 ${
              needsInvert(hotel.logo) ? "brightness-0 invert" : ""
            }`}
          />
        )}

        <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-20 px-6 md:px-12 max-w-[1440px] mx-auto">
          <Link
            to="/hotel"
            data-testid="hotel-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6 w-fit"
          >
            <ArrowLeft size={14} /> Portafoglio
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/70">
              <span className="inline-flex items-center gap-1 text-[#C48F56]">
                {Array.from({ length: hotel.stars }).map((_, i) => (
                  <Star key={i} size={11} fill="currentColor" stroke="none" />
                ))}
              </span>
              <span className="h-px w-10 bg-white/30" />
              {hotel.area}
            </div>
            <h1 className="mt-5 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium">
              {hotel.name}
            </h1>
            {hotel.tagline && (
              <p className="mt-5 font-display italic text-2xl md:text-3xl text-white/80 max-w-2xl">
                {hotel.tagline}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Facts */}
      <section className="relative px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              La struttura
            </div>
            <p className="mt-6 text-white/80 text-xl md:text-2xl leading-relaxed font-display text-balance">
              {hotel.description}
            </p>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <div className="grid grid-cols-2 gap-6">
              <Fact
                icon={<BedDouble size={14} />}
                label="Camere"
                value={hotel.rooms}
              />
              <Fact
                icon={<Star size={14} />}
                label="Categoria"
                value={`${hotel.stars} stelle`}
              />
              <Fact
                icon={<MapPin size={14} />}
                label="Zona"
                value={hotel.area}
              />
              <Fact
                icon={<ArrowUpRight size={14} />}
                label="Apertura"
                value="Operativo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section
          data-testid="hotel-gallery"
          className="relative px-6 md:px-12 max-w-[1440px] mx-auto pb-24 md:pb-36"
        >
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
                <span className="h-px w-8 bg-[#C48F56]" />
                Galleria
              </div>
              <h2 className="mt-5 font-display text-5xl md:text-6xl tracking-tightest">
                Spazi & <span className="text-[#C48F56]">atmosfere.</span>
              </h2>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 pb-2">
              {gallery.length} foto
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[260px]">
            {gallery.map((img, i) => {
              const big = i % 5 === 0;
              return (
                <motion.button
                  type="button"
                  key={`${img}-${i}`}
                  onClick={() => openLightbox(i)}
                  data-testid={`gallery-item-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{
                    duration: 0.6,
                    delay: (i % 4) * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`group relative overflow-hidden rounded-xl border border-white/10 bg-[#0F0F14] ${
                    big ? "md:col-span-2 md:row-span-2" : ""
                  }`}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                    style={{ backgroundImage: `url(${resolveImage(img)})` }}
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                  <div className="absolute bottom-3 right-3 h-8 w-8 rounded-full bg-white/0 group-hover:bg-white group-hover:text-black grid place-items-center transition-colors">
                    <ArrowUpRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </section>
      )}

      {/* Related hotels */}
      {relatedHotels.length > 0 && (
        <section className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-20 md:py-28">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
                <span className="h-px w-8 bg-[#C48F56]" />
                Esplora
              </div>
              <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tightest">
                Altri hotel del gruppo.
              </h2>
            </div>
            <Link
              to="/hotel"
              className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white hover:text-black transition-colors"
            >
              Tutto il portafoglio <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {relatedHotels.map((h) => (
              <Link
                key={h.id}
                to={`/hotel/${h.slug}`}
                data-testid={`related-hotel-${h.slug}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F14] hover:border-white/25 transition-colors"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                    style={{ backgroundImage: `url(${resolveImage(h.image)})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  {h.logo && (
                    <img
                      src={h.logo}
                      alt={h.name}
                      className={`absolute bottom-3 right-3 h-7 w-auto max-w-[80px] object-contain opacity-90 ${
                        needsInvert(h.logo) ? "brightness-0 invert" : ""
                      }`}
                    />
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl tracking-tight">
                    {h.name}
                  </h3>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {"★".repeat(h.stars)} · {h.area} · {h.rooms} camere
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox — rendered via Portal to escape PageTransition's
          stacking context (filter/transform breaks `position: fixed`). */}
      {createPortal(
        <AnimatePresence>
        {lightbox >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm grid place-items-center p-4"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close"
              className="absolute top-6 right-6 h-10 w-10 rounded-full bg-white/10 backdrop-blur grid place-items-center hover:bg-white hover:text-black z-10"
              aria-label="Chiudi"
            >
              <X size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImg();
              }}
              data-testid="lightbox-prev"
              className="absolute left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 backdrop-blur grid place-items-center hover:bg-white hover:text-black z-10"
              aria-label="Precedente"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImg();
              }}
              data-testid="lightbox-next"
              className="absolute right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 backdrop-blur grid place-items-center hover:bg-white hover:text-black z-10"
              aria-label="Successiva"
            >
              <ChevronRight size={18} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              src={resolveImage(gallery[lightbox])}
              alt=""
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-[0_20px_80px_rgba(0,0,0,0.5)]"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white/70">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                {hotel.name}
              </span>
              <span className="h-px w-8 bg-white/20" />
              <span className="font-mono text-xs tabular-nums">
                <span className="text-[#C48F56]">{String(lightbox + 1).padStart(2, "0")}</span>
                <span className="text-white/30"> / {String(gallery.length).padStart(2, "0")}</span>
              </span>
            </div>
            <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden md:block">
              ← → navigare · Esc chiudere
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0F0F14] p-5">
      <div className="flex items-center gap-2 text-[#C48F56] text-[10px] uppercase tracking-[0.25em] font-mono">
        {icon}
        {label}
      </div>
      <div className="mt-3 font-display text-3xl tracking-tight">{value}</div>
    </div>
  );
}
